import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { clinicInfo } from '../mock';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', path: '/' },
    { 
      name: 'Family Practice', 
      path: '/family-practice',
      submenu: [
        { name: 'For Patients', path: '/for-patients' },
        { name: 'Team', path: '/team' }
      ]
    },
    { name: 'Pharmacy', path: '/pharmacy' },
    { 
      name: 'Rehab Services', 
      path: '/rehab-services',
      submenu: [
        { name: 'Physiotherapy', path: '/physiotherapy' },
        { name: 'Chiropractic', path: '/chiropractic' },
        { name: 'Massage Therapy', path: '/massage-therapy' },
        { name: 'Custom Orthotics', path: '/custom-orthotics' }
      ]
    },
    { name: 'After Hours', path: '/after-hours' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActivePath = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 w-full bg-white border-b border-gray-200 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_wellness-hub-468/artifacts/m2yep3lc_Polar%20Logo.png" 
              alt="Polar iHealth Logo" 
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navigation.map((item) => (
              <div key={item.name} className="relative group">
                <Link
                  to={item.path}
                  className={`text-sm font-medium transition-colors hover:text-[#006D73] ${
                    isActivePath(item.path) ? 'text-[#006D73]' : 'text-[#36454F]'
                  }`}
                >
                  {item.name}
                </Link>
                {item.submenu && (
                  <div className="absolute left-0 mt-2 w-48 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 bg-white border border-gray-200 rounded-lg shadow-lg">
                    <div className="py-2">
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.path}
                          className="block px-4 py-2 text-sm text-[#36454F] hover:bg-gray-50 hover:text-[#006D73] transition-colors"
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <a href={`tel:${clinicInfo.phone}`}>
              <Button variant="outline" className="border-[#006D73] text-[#006D73] hover:bg-[#006D73] hover:text-white transition-all">
                <Phone className="h-4 w-4 mr-2" />
                {clinicInfo.phone}
              </Button>
            </a>
            <Link to="/book">
              <Button className="bg-[#006D73] text-white hover:bg-[#005a5f] transition-all">
                Book Appointment
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-[#36454F] hover:text-[#006D73] transition-colors"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-4">
              {navigation.map((item) => (
                <div key={item.name}>
                  <Link
                    to={item.path}
                    className={`block text-sm font-medium transition-colors hover:text-[#006D73] ${
                      isActivePath(item.path) ? 'text-[#006D73]' : 'text-[#36454F]'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                  {item.submenu && (
                    <div className="ml-4 mt-2 space-y-2">
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.path}
                          className="block text-sm text-[#36454F] hover:text-[#006D73] transition-colors"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-4 space-y-2">
                <a href={`tel:${clinicInfo.phone}`}>
                  <Button variant="outline" className="w-full border-[#006D73] text-[#006D73]">
                    <Phone className="h-4 w-4 mr-2" />
                    Call Now
                  </Button>
                </a>
                <Link to="/book" onClick={() => setIsMenuOpen(false)}>
                  <Button className="w-full bg-[#006D73] text-white">
                    Book Appointment
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;